/**
 * @file trackerPopupComponent.js
 * @description Cookie/analytics consent popup web component.
 */

/*
* Behavior now works like this: first visit gets no tracking snippet by default;
* the popup submits Accept/Reject with CSRF protection, stores the local
* sessionStorage flags, records the audit row, and sets the signed consent cookie.
* On later navigations, granted consent causes the server to inject GA again,
* while denied or unset consent keeps it excluded. New tabs can now bootstrap from
* the server consent-state endpoint even though sessionStorage is tab-local, and
* granted consent is also passed into GA init via server-rendered script metadata
* so cross-tab granted state does not fall back to denied in
* public/js/ga4-init.js.
*/

import { html, css, LitElement } from "lit";

/**
 * Tracks and stores client-side consent decisions for analytics.
 */
export default class TrackerPopup extends LitElement {
  constructor() {
    super();

    this.popupIsHidden = true;
    this.isSubmitting = false;
    this.csrfToken = null;
  }

  static get properties() {
    return {
      popupIsHidden: { type: Boolean, attribute: false },
      isSubmitting: { type: Boolean, attribute: false },
    };
  }

  static get styles() {
    return css`
      :host {
        display: block;
        align-items: center;
        justify-content: center;
        width: 40vw;
        position: fixed;
        bottom: 2rem;
        left: 2rem;
        height: max-content;
        z-index: 5000;
        background-color: #005B99;
        color: #fff;
        filter: drop-shadow(0 0 0.3rem black);
      }

      button {
        width: max-content;
      }

      .tracker__action-bar {
        width: max-content;
        margin: 0 2rem 0 auto;
      }

      .link--on-color{
        color: white;
        text-decoration: underline;
      }

      .link--on-color:hover {
        color: var(--color-blue);
      }

      @media only screen and (max-width: 900px) {
        :host {
          width: 50vw;
          left: 0;
        }
      }

      @media only screen and (max-width: 500px) {
        :host {
          width: 100vw;
          left: 0;
        }
      }
    `;
  }

  /**
   * Renders popup content unless hidden by session state.
   *
   * @returns {import('lit').TemplateResult}
   */
  render() {
    return this.popupIsHidden ?
    html`` :
    html`
    <link rel="stylesheet" href="/index.css" />
    <p class="content">
      We use analytics to enhance user experience and to analyze performance
      and traffic on our website. We also share information about your use of
      our site with our social media, advertising and analytics partners. For more information, please see our:
      <a href="/privacy-policy" target="_blank" rel="noopener" class="link--on-color">Privacy Policy</a>
    </p>
    <div class="tracker__action-bar">
      <button ?disabled="${this.isSubmitting}" @click="${this.handleRejectCookiesClick}" class="button__ghost--on-color">
        Reject
      </button>
      <button ?disabled="${this.isSubmitting}" @click="${this.handleAcceptClick}" class="button__primary">Accept</button>
    </div>`;
  }

  /**
   * Initializes popup visibility based on prior session choice.
   *
   * @returns {void}
   */
  connectedCallback() {
    super.connectedCallback();
    // const storedValue = sessionStorage.getItem("popup-hidden");
    // if (storedValue && storedValue !== null)
    //   if (storedValue === "true") {
    //     this.popupIsHidden = true;
    //     return;
    //   }
    void this.initializeConsentState();
  }

  async initializeConsentState() {
    try {
      const response = await fetch('/api/v1/privacy-log/consent-state');
      if (!response.ok) {
        this.popupIsHidden = false;
        return;
      }

      const data = await response.json();
      if (data?.decision === 'granted' || data?.decision === 'denied') {
        this.persistLocalConsent(data.decision);
        this.popupIsHidden = true;
        this.updateClientConsent(data.decision);
        return;
      }
    } catch (error) {
      console.error('Failed to initialize consent state:', error);
    }

    this.popupIsHidden = false;
  }

  persistLocalConsent(decision) {
    sessionStorage.setItem('popup-hidden', true);
    sessionStorage.setItem('ga4-consent', decision);
  }

  updateClientConsent(decision) {
    if (typeof window.gtag !== 'function') {
      return;
    }

    const consentValue = decision === 'granted' ? 'granted' : 'denied';
    window.gtag('consent', 'update', {
      ad_storage: consentValue,
      analytics_storage: consentValue,
    });
  }

  async fetchCsrfToken() {
    if (window.csrfTokenPromise) {
      this.csrfToken = await window.csrfTokenPromise;
      return;
    }

    if (
      window.sharedCsrfToken &&
      window.sharedCsrfTokenTimestamp &&
      Date.now() - window.sharedCsrfTokenTimestamp < 300000
    ) {
      this.csrfToken = window.sharedCsrfToken;
      return;
    }

    window.csrfTokenPromise = (async () => {
      const response = await fetch('/api/csrf-token');
      const data = await response.json();

      window.sharedCsrfToken = data.csrfToken;
      window.sharedCsrfTokenTimestamp = Date.now();
      window.csrfTokenPromise = null;

      return data.csrfToken;
    })();

    try {
      this.csrfToken = await window.csrfTokenPromise;
    } catch (error) {
      window.csrfTokenPromise = null;
      throw error;
    }
  }

  async getBrowserSignature() {
    const parts = [
      navigator.userAgent || '',
      navigator.language || '',
      navigator.platform || '',
      Intl.DateTimeFormat().resolvedOptions().timeZone || '',
      `${screen.width}x${screen.height}x${screen.colorDepth}`,
    ];

    const payload = new TextEncoder().encode(parts.join('|'));
    const digest = await crypto.subtle.digest('SHA-256', payload);
    return Array.from(new Uint8Array(digest))
      .map((value) => value.toString(16).padStart(2, '0'))
      .join('');
  }

  async submitConsentDecision(decision) {
    await this.fetchCsrfToken();

    const browserSignature = await this.getBrowserSignature();

    const response = await fetch('/api/v1/privacy-log', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': this.csrfToken,
      },
      body: JSON.stringify({
        decision,
        browserSignature
      }),
    });

    if (!response.ok) {
      throw new Error(`Consent submission failed with status ${response.status}`);
    }
  }

  async handleConsentDecision(decision) {
    if (this.isSubmitting) {
      return;
    }

    this.isSubmitting = true;

    try {
      await this.submitConsentDecision(decision);
      this.persistLocalConsent(decision);
      this.updateClientConsent(decision);
      this.popupIsHidden = true;
    } catch (error) {
      console.error('Failed to record consent decision:', error);
      this.popupIsHidden = false;
    } finally {
      this.isSubmitting = false;
    }
  }


  /**
   * Accepts analytics consent and stores session flags.
   *
   * @returns {void}
   */
  handleAcceptClick() {
    void this.handleConsentDecision('granted');
  }

  /**
   * Rejects analytics consent and stores session flags.
   *
   * @returns {void}
   */
  handleRejectCookiesClick() {
    void this.handleConsentDecision('denied');
  }
}

customElements.define("tracker-popup", TrackerPopup);
